import debounce from 'just-debounce';

/**
 * @class CartDrawer
 * @classdesc The cart drawer component is used to handle the cart drawer functionality.
 *
 * @extends Core.Component
 */
export default class CartDrawer extends Core.Component {
  constructor(el, context) {
    super(el, context);

    // Event listeners that have a different lifecycle to the component
    this._cartDrawerEventListeners = [];

    this.cartDrawer = this.el;
    this.cartDrawerErrors = this.el.querySelector('[data-cart-drawer-form-errors]');
    this.cartDrawerItemErrors = el.querySelectorAll('[data-cart-drawer-form-item-error]');

    this.state = {
      item_count: this.data.item_count,
    };
  }

  onMount() {
    const cartDrawerEl = this.el.querySelector('[data-cart-drawer]');

    this._fetchCartDrawer = Promise.resolve(cartDrawerEl);
    this.drawerModal = Core.Components.getComponent('drawer-modal');

    if (!this.drawerModal) return;

    this.drawerFlyout = this.drawerModal.createFlyout();
    this.enabled = this.data.enabled;

    this._populateFlyout(cartDrawerEl);
  }

  onDismount() {
    super.onDismount();

    if (this.drawerFlyout) {
      this.drawerFlyout.then(drawerFlyout => drawerFlyout.destroy());
    }
  }

  async prefetch() {
    // We fetch the cart drawer template and
    // reapply it to the drawer to get a unique instance of the cart drawer form
    this._fetchCartDrawer = this._fetchCartDrawer
      .then(() => fetch(`${window.PXUTheme.routes.cart_url}?section_id=cart-drawer-template`))
      .then(response => response.text()) // Return text string of the HTML
      .then(html => (
        // Convert the HTML string into a document object
        new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-cart-drawer]')
      ));

    return this._fetchCartDrawer;
  }

  /**
   * Opens a cart drawer using a drawer modal
   *
   * @param {HTMLNode} trigger The trigger that was clicked to open the cart drawer
   */
  async open(trigger, refresh = true, itemsAdded = null) {
    if (!this.enabled || !this.drawerFlyout) return;

    const drawerFlyout = await this.drawerFlyout;

    const fetchCartDrawer = (refresh
      ? this.prefetch()
      : this._fetchCartDrawer);

    fetchCartDrawer
      .then(cartDrawerEl => this._populateFlyout(cartDrawerEl, itemsAdded))
      .then(() => {
        this.drawerModal.closeAllFlyouts();
        this.drawerModal.openFlyout(drawerFlyout, trigger);
      });
  }

  async _populateFlyout(cartDrawerEl, itemsAdded = false) {
    const drawerFlyout = await this.drawerFlyout;

    const cartHeadingFragment = cartDrawerEl
      .querySelector('[data-cart-drawer-heading-template]')
      .content
      .cloneNode(true);

    drawerFlyout.populateHeading(cartHeadingFragment, true);

    if (itemsAdded) {
      const notificationFragment = cartDrawerEl.querySelector('[data-cart-drawer-notification-template]').content.cloneNode(true);
      const notificationFragmentMessageText = notificationFragment.querySelector('[data-cart-drawer-notification-message-text]');
      const notificationMessageSingular = notificationFragmentMessageText.querySelector('[data-cart-drawer-notification-message-atc-singular]');
      const notificationMessagePlural = notificationFragmentMessageText.querySelector('[data-cart-drawer-notification-message-atc-plural]');

      if (itemsAdded.length === 1) {
        notificationMessageSingular.innerText = notificationMessageSingular
          .innerText
          .replace('||title||', itemsAdded[0].title);
        notificationMessagePlural.remove();
      } else {
        notificationMessagePlural.innerText = notificationMessagePlural.innerText.replace('||count||', itemsAdded.length);
        notificationMessageSingular.remove();
      }

      drawerFlyout.populateNotification(notificationFragment, true);
    }

    this._destroyCartDrawerListeners();

    drawerFlyout.populateContent(cartDrawerEl, true);

    Core
      .Components
      .awaitComponents(cartDrawerEl)
      .then(components => {
        const productCrossSell = components.find(component => component.name === 'product-cross-sell');

        if (productCrossSell) {
          productCrossSell.fetchRecommendations('cart-drawer-template');
        }
      })
      .then(() => this._createCartDrawerListeners(cartDrawerEl));
  }

  _destroyCartDrawerListeners() {
    this._cartDrawerEventListeners.forEach(cartDrawerEventListener => (
      this.events.unregister(cartDrawerEventListener)
    ));

    this._cartDrawerEventListeners = [];
  }

  /**
   * Initializes the listeners for the cart drawer content.
   *
   * @private
   */
  _createCartDrawerListeners(cartDrawerEl) {
    const cartDrawerCount = cartDrawerEl.querySelector('[data-cart-drawer-count]');
    const cartRows = cartDrawerEl.querySelectorAll('[data-cart-drawer-form-item]');
    const cartNotes = cartDrawerEl.querySelector('[data-cart-notes] [data-component="input-textarea"]');
    const cartDrawerUpdateEvent = this.events.register(
      document,
      'cart:update',
      e => {
        if (cartDrawerCount) {
          cartDrawerCount.innerHTML = e.detail.count;
        }
      },
    );

    this._cartDrawerEventListeners.push(cartDrawerUpdateEvent);

    cartRows.forEach(row => {
      const lineItemKey = row.dataset.cartDrawerFormItem;
      const quantitySelector = row.querySelector('[data-component="quantity-selector"]');
      const buttonRemoveItem = row.querySelector('[data-remove]');

      const quantityChangeEvent = this.events.register(
        quantitySelector,
        'quantity:update',
        e => {
          const { quantity } = e.detail;

          if (quantity === 0) {
            this
              ._removeRow(row)
              .then(() => this._updateLineItem(lineItemKey, 0));
          } else {
            this._updateLineItem(lineItemKey, quantity);
          }
        },
      );

      const removeClickEvent = this.events.register(
        buttonRemoveItem,
        'click',
        e => {
          e.preventDefault();
          this
            ._removeRow(row)
            .then(() => this._updateLineItem(lineItemKey, 0));
        },
      );

      this._cartDrawerEventListeners.push(quantityChangeEvent, removeClickEvent);
    });

    if (cartNotes) {
      const cartNoteChangeEvent = this.events.register(
        cartNotes,
        'input',
        debounce(e => this._updateNote(e), 500),
      );

      this._cartDrawerEventListeners.push(cartNoteChangeEvent);
    }
  }

  /**
   * Refreshes the cart content based off of the drawer cart template
   *
   * @param {Number} lineItemKey - The unique key of the line item to update.
   * @param {Number} itemCount - The current quantity of items in the cart
   * @private
   */
  _refreshCart(response, lineItemKey) {
    const { item_count: itemCount, sections } = response;
    const cartDrawerEl = new DOMParser()
      .parseFromString(sections['cart-drawer-template'], 'text/html')
      .querySelector('[data-cart-drawer]');

    this
      ._populateFlyout(cartDrawerEl)
      .then(() => {
        if (this.state.item_count === itemCount) {
          Core
            .Components
            .awaitComponents(cartDrawerEl)
            .then(() => {
              const row = cartDrawerEl.querySelector(`[data-cart-drawer-form-item="${lineItemKey}"]`);
              const targetSelector = Core.Components.getComponent(row.querySelector('[data-component="quantity-selector"]'));
              const inputValue = targetSelector.getValue();

              row
                .querySelector('[data-cart-drawer-form-item-error]')
                .innerHTML = this.data.locales.quantityError.replace('{{ quantity }}', inputValue);
            });
        }

        this.setState({
          item_count: itemCount,
        });

        const updateEvent = new CustomEvent('cart:update', { detail: { count: itemCount }, bubbles: true });
        this.el.dispatchEvent(updateEvent);
      });
  }

  /**
   * Updates the cart with the new quantity and refreshes the innerHTML content.
   *
   * @param {number} lineItemKey - The unique key of the line item to update.
   * @param {object} e - The event object triggered.
   * @private
   */
  _updateLineItem(key, quantity) {
    const formData = {
      id: key,
      quantity,
      sections: 'cart-drawer-template',
      sections_url: `${window.PXUTheme.routes.cart_url}`,
    };

    fetch(`${window.PXUTheme.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(formData),
    })
      .then(response => response.json())
      .then(response => this._refreshCart(response, key))
      .catch(error => this._handleError(error));
  }

  /**
   * Handles general error messaging.
   *
   * @param {String} error - The error message to display.
   * @private
   */
  _handleError(error) {
    this.cartDrawerErrors.style.display = 'block';
    this.cartDrawerErrors.innerHTML = this.data.locales.error;

    console.error(error);
  }

  /**
   * Clear error messaging from the cart section.
   *
   * @private
   */
  _clearErrors() {
    this.cartDrawerErrors.style.display = 'none';
    this.cartDrawerErrors.innerHTML = '';
    this.cartDrawerItemErrors.forEach(error => {
      error.innerHTML = '';
    });
  }

  /**
   * Animates the removal of items from the cart
   *
   * @param {object} data - The data object containing the id, key, and row of the item to remove.
   * @private
   */
  _removeRow(rowEl) {
    const animateRemoval = Core.Animations.transition({ el: rowEl, state: 'active' });

    rowEl.style.setProperty('--open-height', `${rowEl.scrollHeight}px`);

    return animateRemoval
      .animateTo('fade')
      .then(() => animateRemoval.animateTo('remove'))
      .then(() => rowEl.remove());
  }

  _updateNote(e) {
    fetch(window.PXUTheme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ note: e.target.value }),
    });
  }
}
